import { Avatar, Badge } from "@hakistudio/hakiui";
import { formatDistanceToNow } from "date-fns";
import { Check, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useAddComment, useComments, useDeleteComment, useResolveComment } from "@/lib/teams";
import { Input } from "@/components/ui/Field";

export function CommentsPanel({
  noteId,
  canModerate,
  currentUserId,
}: {
  noteId: string;
  canModerate: boolean;
  currentUserId: string | null;
}) {
  const { data } = useComments(noteId);
  const add = useAddComment(noteId);
  const resolve = useResolveComment(noteId);
  const remove = useDeleteComment(noteId);
  const [body, setBody] = useState("");
  const [showResolved, setShowResolved] = useState(false);

  const all = data?.comments ?? [];
  const visible = showResolved ? all : all.filter((c) => !c.resolvedAt);
  const resolvedCount = all.length - all.filter((c) => !c.resolvedAt).length;

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-6">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-ink-muted">
          Comments{all.length > 0 ? ` (${all.length})` : ""}
        </h2>
        {resolvedCount > 0 ? (
          <button
            onClick={() => setShowResolved((v) => !v)}
            className="text-xs text-ink-faint underline-offset-4 hover:underline"
          >
            {showResolved ? "Hide" : "Show"} {resolvedCount} resolved
          </button>
        ) : null}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!body.trim()) return;
          add.mutate(body.trim(), { onSuccess: () => setBody("") });
        }}
      >
        <Input
className="flex-1"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Leave a comment"
        />
        <Button type="submit" disabled={add.isPending || !body.trim()}>Post</Button>
      </form>

      {visible.length === 0 ? (
        <p className="text-sm text-ink-faint">No comments yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {visible.map((c) => (
            <li
              key={c.id}
              className={cn(
                "flex items-start gap-3 rounded-2xl border border-line bg-raised px-4 py-3",
                c.resolvedAt && "opacity-55",
              )}
            >
              <Avatar name={c.authorName ?? "?"} size="sm" radius="full" />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-ink">{c.authorName ?? "Someone"}</span>
                  <span className="text-xs text-ink-faint">
                    {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                  </span>
                  {c.resolvedAt ? <Badge size="sm" variant="soft">resolved</Badge> : null}
                </span>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-ink-muted">{c.body}</p>
              </span>

              {canModerate ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={c.resolvedAt ? "Reopen" : "Resolve"}
                  onClick={() => resolve.mutate({ id: c.id, resolved: !c.resolvedAt })}
                >
                  <Check className="h-4 w-4" />
                </Button>
              ) : null}
              {canModerate || c.authorId === currentUserId ? (
                <Button variant="ghost" size="icon" aria-label="Delete comment" onClick={() => remove.mutate(c.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
